import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "sonner";

import Login from "./pages/Login";
import Register from "./pages/Register";
import PharmacyDashboard from "./pages/PharmacyDashboard";
import Needs from "./pages/Needs";
import Replacements from "./pages/Replacements";
import ReplacementProfile from "./pages/ReplacementProfile";
import Alerts from "./pages/Alerts";
import Bookings from "./pages/Bookings";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import MissionsFeed from "./pages/MissionsFeed";
import MyMissions from "./pages/MyMissions";
import Profile from "./pages/Profile";
import Messages from "./pages/Messages";
import CalendarWeek from "./pages/CalendarWeek";
import ProfileWizard from "./pages/ProfileWizard";
import MyProfile from "./pages/MyProfile";
import MyAvailabilities from "./pages/MyAvailabilities";
import MyDocuments from "./pages/MyDocuments";
import Preferences from "./pages/Preferences";
import ResetPassword from "./pages/ResetPassword";
import CandidateLayout from "./components/CandidateLayout";

function Loading() {
  return <div className="min-h-screen grid place-items-center text-sm text-[#6B726A]">Chargement…</div>;
}

function Private({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "pharmacy" ? "/dashboard" : "/missions"} replace />;
  }
  return children;
}

/**
 * Single mounted parent for ALL routes that the candidate can access
 * (candidate-only AND shared with pharmacy). For candidates, renders the
 * persistent CandidateLayout once; for pharmacies, falls through to <Outlet/>
 * so each pharmacy page renders its own internal Sidebar.
 *
 * Because this component instance is the same across every nested route,
 * the CandidateSidebar is mounted once and never re-created when the user
 * navigates between pages — only the <Outlet/> content changes.
 */
function SharedShell() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "replacement") return <CandidateLayout />;
  return <Outlet />;
}

/** Lightweight role gate to use inside SharedShell for candidate-only paths */
function RoleGate({ role, children }) {
  const { user } = useAuth();
  if (user && role && user.role !== role) {
    return <Navigate to={user.role === "pharmacy" ? "/dashboard" : "/missions"} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "pharmacy" ? "/dashboard" : "/missions"} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ResetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Pharmacy-only routes */}
            <Route path="/dashboard" element={<Private role="pharmacy"><PharmacyDashboard /></Private>} />
            <Route path="/needs" element={<Private role="pharmacy"><Needs /></Private>} />
            <Route path="/replacements" element={<Private role="pharmacy"><Replacements /></Private>} />
            <Route path="/replacements/:id" element={<Private role="pharmacy"><ReplacementProfile /></Private>} />
            <Route path="/billing" element={<Private role="pharmacy"><Pricing /></Private>} />
            <Route path="/pricing" element={<Private role="pharmacy"><Pricing /></Private>} />
            <Route path="/billing/success" element={<Private role="pharmacy"><BillingSuccess /></Private>} />
            <Route path="/calendar" element={<Private role="pharmacy"><CalendarWeek /></Private>} />
            <Route path="/recruitments" element={<Navigate to="/needs" replace />} />
            <Route path="/officine" element={<Private role="pharmacy"><Profile /></Private>} />

            {/*
              All routes accessible by candidates live under a SINGLE SharedShell parent.
              For replacements -> CandidateLayout (one persistent sidebar).
              For pharmacies   -> bare <Outlet/> (pages keep their own Sidebar).
            */}
            <Route element={<SharedShell />}>
              {/* shared with pharmacy */}
              <Route path="/messages" element={<Messages />} />
              <Route path="/bookings" element={<Bookings />} />
              <Route path="/alerts" element={<Alerts />} />
              <Route path="/settings" element={<Profile />} />
              <Route path="/profile" element={<ProfileRouter />} />

              {/* candidate-only — gated with RoleGate to redirect pharmacies away */}
              <Route path="/missions" element={<RoleGate role="replacement"><MissionsFeed /></RoleGate>} />
              <Route path="/my-missions" element={<RoleGate role="replacement"><MyMissions /></RoleGate>} />
              <Route path="/profile/edit" element={<RoleGate role="replacement"><ProfileWizard /></RoleGate>} />
              <Route path="/availabilities" element={<RoleGate role="replacement"><MyAvailabilities /></RoleGate>} />
              <Route path="/documents" element={<RoleGate role="replacement"><MyDocuments /></RoleGate>} />
              <Route path="/preferences" element={<RoleGate role="replacement"><Preferences /></RoleGate>} />
            </Route>

            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

function ProfileRouter() {
  const { user } = useAuth();
  return user?.role === "replacement" ? <MyProfile /> : <Profile />;
}
